import React, { Component } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Container,
  Grid,
  Image,
  Heading,
  Card,
  CardBody,
  Icon,
  SimpleGrid,
  Badge
} from '@chakra-ui/react';
import { FaRocket, FaBrain, FaBullseye, FaChartLine, FaStar, FaCheckCircle } from 'react-icons/fa';

class LandingPage extends Component {
  scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    return (
      <Box minH="100vh" bg="gray.50">
        {/* Hero Section with Gradient Background */}
        <Box bgGradient="linear(to-br, brand.400, purple.300)" py={{ base: 16, md: 32, lg: 48 }} px={{ base: 4, md: 6 }} position="relative">
          {/* Navigation Tabs - Fixed top right */}
          <HStack justify="flex-end" spacing={{ base: 2, md: 8 }} position="absolute" top={4} right={6} display={{ base: 'none', md: 'flex' }}>
            <Button
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => this.scrollToSection('how-it-works')}
              size={{ base: 'sm', md: 'md' }}
            >
              How it Works
            </Button>
            <Button
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => this.scrollToSection('reviews')}
              size={{ base: 'sm', md: 'md' }}
            >
              Reviews
            </Button>
            <Button
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => this.scrollToSection('pricing')}
              size={{ base: 'sm', md: 'md' }}
            >
              Pricing
            </Button>
          </HStack>

          <Container maxW="6xl">
            <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
              {/* <Badge 
                bg="whiteAlpha.200" 
                color="white" 
                px={4} 
                py={2} 
                borderRadius="full" 
                fontSize="sm"
              >
                AI-Powered Personal Development
              </Badge> */}
              
              <Heading
                as="h1"
                size={{ base: 'xl', md: '2xl', lg: '3xl' }}
                fontWeight="800"
                color="white"
                lineHeight="shorter"
                px={{ base: 2, md: 0 }}
              >
                Grow your thinking, one exercise at a time
              </Heading>

              <Text fontSize={{ base: 'md', md: 'lg', lg: 'xl' }} color="whiteAlpha.900" maxW="2xl" lineHeight="tall" px={{ base: 2, md: 0 }}>
                Build cognitive skills through AI-guided progressive exercises. Master critical thinking, emotional intelligence, communication, and mental frameworks—all through text-based practice.
              </Text>

              <HStack spacing={4} pt={4} justify="center" flexWrap="wrap">
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  bgGradient="linear(to-r, brand.600, purple.600)"
                  color="white"
                  _hover={{
                    bgGradient: "linear(to-r, brand.600, purple.600)",
                    transform: "translateY(-2px)",
                    shadow: "xl"
                  }}
                  _active={{
                    bgGradient: "linear(to-r, brand.700, purple.700)",
                    transform: "translateY(0px)"
                  }}
                  onClick={() => this.props.onRouteChange('register')}
                  leftIcon={<FaRocket />}
                  borderRadius="full"
                  px={{ base: 6, md: 8 }}
                  py={{ base: 5, md: 6 }}
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="bold"
                  shadow="lg"
                  borderColor="whiteAlpha.300"
                >
                  Start Your Journey
                </Button>
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: "whiteAlpha.200" }}
                  onClick={() => this.props.onRouteChange('login')}
                  borderRadius="full"
                  px={{ base: 6, md: 8 }}
                  py={{ base: 5, md: 6 }}
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="bold"
                >
                  Sign In
                </Button>
              </HStack>

              <Text fontSize={{ base: 'xs', md: 'sm' }} color="whiteAlpha.800">
                Join others building stronger minds daily
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* How It Works Section */}
        <Box id="how-it-works" bg="gray.50" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="6xl">
            <VStack spacing={{ base: 10, md: 16 }}>
              <VStack spacing={{ base: 3, md: 4 }} textAlign="center">
                <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700">
                  How <Text as="span" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">Grow</Text> Works
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="2xl">
                  Three simple steps to strengthen your cognitive abilities
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 8, md: 10 }} w="full">
                <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
                  <Box
                    p={{ base: 4, md: 6 }}
                    bg="brand.50"
                    borderRadius="full"
                    border="4px solid"
                    borderColor="brand.100"
                  >
                    <Icon as={FaBullseye} boxSize={{ base: 8, md: 10 }} color="brand.600" />
                  </Box>
                  <VStack spacing={{ base: 2, md: 3 }}>
                    <Heading size={{ base: 'md', md: 'lg' }} color="brand.600">1. Choose Your Skill</Heading>
                    <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                      Pick a cognitive skill to develop—from critical thinking to emotional intelligence. Our AI creates a personalized path just for you.
                    </Text>
                  </VStack>
                </VStack>

                <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
                  <Box
                    p={{ base: 4, md: 6 }}
                    bg="purple.50"
                    borderRadius="full"
                    border="4px solid"
                    borderColor="purple.100"
                  >
                    <Icon as={FaBrain} boxSize={{ base: 8, md: 10 }} color="purple.600" />
                  </Box>
                  <VStack spacing={{ base: 2, md: 3 }}>
                    <Heading size={{ base: 'md', md: 'lg' }} color="purple.600">2. Practice Through Writing</Heading>
                    <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                      Get progressive text-based exercises that build your mental capabilities. Writing and reflection are the perfect medium for cognitive growth.
                    </Text>
                  </VStack>
                </VStack>

                <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
                  <Box
                    p={{ base: 4, md: 6 }}
                    bg="green.50"
                    borderRadius="full"
                    border="4px solid"
                    borderColor="green.100"
                  >
                    <Icon as={FaChartLine} boxSize={{ base: 8, md: 10 }} color="green.600" />
                  </Box>
                  <VStack spacing={{ base: 2, md: 3 }}>
                    <Heading size={{ base: 'md', md: 'lg' }} color="green.600">3. Build Mental Strength</Heading>
                    <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                      Track your consistency and watch your thinking evolve. Small daily practices compound into powerful cognitive abilities.
                    </Text>
                  </VStack>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* App Screenshots Section */}
        <Box bg="white" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="6xl">
            <VStack spacing={{ base: 10, md: 16 }}>
              <VStack spacing={{ base: 3, md: 4 }} textAlign="center">
                <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700">
                  See <Text as="span" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">Grow</Text> in Action
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="2xl">
                  Experience the intuitive interface designed for your personal growth journey
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 8 }} w="full">
                {/* Dashboard Screenshot */}
                <VStack spacing={6}>
                  <Box
                    w="full"
                    h="300px"
                    borderRadius="xl"
                    border="3px solid"
                    borderColor="brand.200"
                    position="relative"
                    overflow="hidden"
                    shadow="lg"
                    _hover={{ shadow: "xl", transform: "translateY(-2px)" }}
                    transition="all 0.3s ease"
                  >
                    {/* ToDo: Replace with dashboard screenshot */}
                    <Image
                      src="/assets/screenshots/dashboard.png" // ToDo: Add dashboard screenshot file here
                      alt="Dashboard Screenshot"
                      w="full"
                      h="full"
                      objectFit="cover"
                      fallback={
                        <Box
                          w="full"
                          h="full"
                          bg="gray.100"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <VStack spacing={2} color="gray.500">
                            <Icon as={FaChartLine} boxSize={12} />
                            <Text fontSize="lg" fontWeight="semibold">
                              Dashboard Screenshot
                            </Text>
                          </VStack>
                        </Box>
                      }
                    />
                  </Box>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="md" color="brand.600">Your Progress Dashboard</Heading>
                    <Text color="gray.600" fontSize="sm">
                      Track all your goals and see your growth at a glance
                    </Text>
                  </VStack>
                </VStack>

                {/* Create Goal Screenshot */}
                <VStack spacing={6}>
                  <Box
                    w="full"
                    h="300px"
                    borderRadius="xl"
                    border="3px solid"
                    borderColor="purple.200"
                    position="relative"
                    overflow="hidden"
                    shadow="lg"
                    _hover={{ shadow: "xl", transform: "translateY(-2px)" }}
                    transition="all 0.3s ease"
                  >
                    {/* ToDo: Replace with create goal screenshot */}
                    <Image
                      src="/assets/screenshots/create-goal.png" // ToDo: Add create goal screenshot file here
                      alt="Create Goal Screenshot"
                      w="full"
                      h="full"
                      objectFit="cover"
                      fallback={
                        <Box
                          w="full"
                          h="full"
                          bg="gray.100"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <VStack spacing={2} color="gray.500">
                            <Icon as={FaBullseye} boxSize={12} />
                            <Text fontSize="lg" fontWeight="semibold">
                              Create Goal Screenshot
                            </Text>
                          </VStack>
                        </Box>
                      }
                    />
                  </Box>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="md" color="purple.600">Goal Creation</Heading>
                    <Text color="gray.600" fontSize="sm">
                      Simple and intuitive goal setup process
                    </Text>
                  </VStack>
                </VStack>

                {/* Exercise View Screenshot */}
                <VStack spacing={6}>
                  <Box
                    w="full"
                    h="300px"
                    borderRadius="xl"
                    border="3px solid"
                    borderColor="green.200"
                    position="relative"
                    overflow="hidden"
                    shadow="lg"
                    _hover={{ shadow: "xl", transform: "translateY(-2px)" }}
                    transition="all 0.3s ease"
                  >
                    {/* ToDo: Replace with exercise view screenshot */}
                    <Image
                      src="/assets/screenshots/exercise-view.png" // ToDo: Add exercise view screenshot file here
                      alt="Exercise View Screenshot"
                      w="full"
                      h="full"
                      objectFit="cover"
                      fallback={
                        <Box
                          w="full"
                          h="full"
                          bg="gray.100"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <VStack spacing={2} color="gray.500">
                            <Icon as={FaBrain} boxSize={12} />
                            <Text fontSize="lg" fontWeight="semibold">
                              Exercise View Screenshot
                            </Text>
                          </VStack>
                        </Box>
                      }
                    />
                  </Box>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="md" color="green.600">AI-Generated Exercises</Heading>
                    <Text color="gray.600" fontSize="sm">
                      Personalized exercises designed just for you
                    </Text>
                  </VStack>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Features Section */}
        <Box bg="gray.50" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="6xl">
            <VStack spacing={{ base: 10, md: 16 }}>
              <VStack spacing={{ base: 3, md: 4 }} textAlign="center">
                <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700">
                  Why Choose <Text as="span" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">Grow</Text>?
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="2xl">
                  The most effective way to build cognitive and mental skills
                </Text>
              </VStack>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={{ base: 4, md: 8 }} w="full">
                <Card>
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack align="start" spacing={{ base: 3, md: 4 }}>
                      <Icon as={FaBrain} boxSize={{ base: 6, md: 8 }} color="brand.500" />
                      <Heading size={{ base: 'md', md: 'lg' }}>Text-Native Learning</Heading>
                      <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                        Writing and reflection aren't a compromise—they're the optimal way to develop thinking skills. Your thoughts need articulation to evolve.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack align="start" spacing={{ base: 3, md: 4 }}>
                      <Icon as={FaBullseye} boxSize={{ base: 6, md: 8 }} color="purple.500" />
                      <Heading size={{ base: 'md', md: 'lg' }}>Cognitive Skills Focus</Heading>
                      <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                        Master skills where text excels: critical thinking, self-awareness, communication, and mental models. No piano lessons—just pure cognitive development.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack align="start" spacing={{ base: 3, md: 4 }}>
                      <Icon as={FaCheckCircle} boxSize={{ base: 6, md: 8 }} color="green.500" />
                      <Heading size={{ base: 'md', md: 'lg' }}>Flexible Scheduling</Heading>
                      <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                        Choose your commitment level. Whether it's 10 minutes or
                        an hour a day, we adapt to your schedule.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack align="start" spacing={{ base: 3, md: 4 }}>
                      <Icon as={FaChartLine} boxSize={{ base: 6, md: 8 }} color="orange.500" />
                      <Heading size={{ base: 'md', md: 'lg' }}>Track Your Growth</Heading>
                      <Text color="gray.600" lineHeight="tall" fontSize={{ base: 'sm', md: 'md' }}>
                        Visualize your progress with detailed insights. See how
                        consistent practice transforms into real results.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              </Grid>
            </VStack>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Box id="reviews" bg="white" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="6xl">
            <VStack spacing={{ base: 10, md: 16 }}>
              <VStack spacing={{ base: 3, md: 4 }} textAlign="center">
                <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700">
                  Join Others Who Are <Text as="span" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">Grow</Text>ing
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                  Real stories from people transforming their lives
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 8 }}>
                <Card bg="white" shadow="lg">
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack spacing={{ base: 3, md: 4 }} align="start">
                      <HStack>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon key={star} as={FaStar} color="yellow.400" boxSize={{ base: 4, md: 5 }} />
                        ))}
                      </HStack>
                      <Text fontStyle="italic" color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
                        "The AI exercises are incredibly personalized. I've made more
                        progress in 3 months than I did in years of self-help books."
                      </Text>
                      <Text fontWeight="bold" color="brand.600" fontSize={{ base: 'sm', md: 'md' }}>
                        Sarah M.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <Card bg="white" shadow="lg">
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack spacing={{ base: 3, md: 4 }} align="start">
                      <HStack>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon key={star} as={FaStar} color="yellow.400" boxSize={{ base: 4, md: 5 }} />
                        ))}
                      </HStack>
                      <Text fontStyle="italic" color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
                        "Finally, a system that adapts to my busy schedule.
                        10 minutes a day is all I need to stay consistent."
                      </Text>
                      <Text fontWeight="bold" color="brand.600" fontSize={{ base: 'sm', md: 'md' }}>
                        Michael R.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <Card bg="white" shadow="lg">
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack spacing={{ base: 3, md: 4 }} align="start">
                      <HStack>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon key={star} as={FaStar} color="yellow.400" boxSize={{ base: 4, md: 5 }} />
                        ))}
                      </HStack>
                      <Text fontStyle="italic" color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
                        "The progressive approach is genius. Each exercise feels
                        challenging but achievable. I'm actually sticking with it!"
                      </Text>
                      <Text fontWeight="bold" color="brand.600" fontSize={{ base: 'sm', md: 'md' }}>
                        Emma L.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Pricing Section */}
        <Box id="pricing" bg="gray.50" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="4xl">
            <VStack spacing={{ base: 10, md: 16 }}>
              <VStack spacing={{ base: 3, md: 4 }} textAlign="center">
                <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700">
                  Simple, Transparent Pricing
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                  Choose the plan that fits your personal development journey
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} w="full">
                {/* Free Plan */}
                <Card shadow="lg" border="2px" borderColor="gray.200">
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack spacing={{ base: 4, md: 6 }} align="start" h="full">
                      <VStack align="start" spacing={2}>
                        <Heading size={{ base: 'md', md: 'lg' }}>Free</Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>Perfect for getting started</Text>
                      </VStack>

                      <HStack align="baseline">
                        <Heading size={{ base: '2xl', md: '3xl' }} color="brand.600">€0</Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>/month</Text>
                      </HStack>

                      <VStack align="start" spacing={{ base: 2, md: 3 }} flex="1">
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Create up to 3 goals</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>AI-generated exercises</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Progress tracking</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Basic support</Text>
                        </HStack>
                      </VStack>

                      <Button
                        size={{ base: 'md', md: 'lg' }}
                        variant="outline"
                        w="full"
                        onClick={() => this.props.onRouteChange('register')}
                      >
                        Get Started Free
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Pro Plan */}
                <Card
                  shadow="xl"
                  border="2px"
                  borderColor="brand.500"
                  position="relative"
                  transform={{ base: 'none', md: 'scale(1.05)' }}
                >
                  <Badge
                    position="absolute"
                    top="-10px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg="brand.500"
                    color="white"
                    px={{ base: 3, md: 4 }}
                    py={1}
                    borderRadius="full"
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Most Popular
                  </Badge>
                  <CardBody p={{ base: 5, md: 8 }}>
                    <VStack spacing={{ base: 4, md: 6 }} align="start" h="full">
                      <VStack align="start" spacing={2}>
                        <Heading size={{ base: 'md', md: 'lg' }}>Pro</Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>For serious personal growth</Text>
                      </VStack>

                      <HStack align="baseline">
                        <Heading size={{ base: '2xl', md: '3xl' }} color="brand.600">€10</Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>/month</Text>
                      </HStack>

                      <VStack align="start" spacing={{ base: 2, md: 3 }} flex="1">
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Unlimited goals</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Advanced AI exercises</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Detailed analytics</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Priority support</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Export your progress</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                          <Text fontSize={{ base: 'sm', md: 'md' }}>Custom scheduling</Text>
                        </HStack>
                      </VStack>

                      <Button
                        size={{ base: 'md', md: 'lg' }}
                        colorScheme="brand"
                        w="full"
                        onClick={() => this.props.onRouteChange('register')}
                      >
                        Start Pro Trial
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <Text textAlign="center" color="gray.500" fontSize={{ base: 'xs', md: 'sm' }}>
                All plans include a 7-day free trial • Cancel anytime
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box bg="white" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="4xl">
            <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
              <Heading size={{ base: 'lg', md: 'xl', lg: '2xl' }} fontWeight="700" color="gray.800">
                Ready to Build a Stronger Mind?
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg', lg: 'xl' }} color="gray.600" maxW="2xl" lineHeight="tall">
                Start developing cognitive skills that will serve you for life. Critical thinking, self-awareness, and mental clarity—all through guided daily practice.
              </Text>
              <HStack spacing={4} pt={4} justify="center" flexWrap="wrap">
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  bgGradient="linear(to-r, brand.600, purple.600)"
                  color="white"
                  _hover={{
                    bgGradient: "linear(to-r, brand.600, purple.600)",
                    transform: "translateY(-2px)",
                    shadow: "xl"
                  }}
                  _active={{
                    bgGradient: "linear(to-r, brand.700, purple.700)",
                    transform: "translateY(0px)"
                  }}
                  onClick={() => this.props.onRouteChange('register')}
                  leftIcon={<FaRocket />}
                  borderRadius="full"
                  px={{ base: 6, md: 8 }}
                  py={{ base: 5, md: 6 }}
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="bold"
                  shadow="lg"
                >
                  Start Free Today
                </Button>
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  variant="outline"
                  borderColor="brand.600"
                  color="brand.600"
                  _hover={{
                    bg: "brand.50",
                    borderColor: "brand.600"
                  }}
                  onClick={() => this.props.onRouteChange('login')}
                  borderRadius="full"
                  px={{ base: 6, md: 8 }}
                  py={{ base: 5, md: 6 }}
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="bold"
                >
                  Sign In
                </Button>
              </HStack>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
                No credit card required • Start in under 2 minutes
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Footer */}
        <Box bgGradient="linear(to-br, brand.400, purple.300)" py={{ base: 10, md: 16 }} px={{ base: 4, md: 6 }} color="white">
          <Container maxW="6xl">
            <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }} gap={{ base: 8, md: 12 }}>
              {/* Brand Section */}
              <VStack align={{ base: 'center', md: 'start' }} spacing={4} textAlign={{ base: 'center', md: 'left' }}>
                <Text as="span" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text">
                  Grow
                </Text>
                <Text color="white" maxW="md" fontSize={{ base: 'sm', md: 'md' }}>
                  Build cognitive skills through AI-guided text exercises. Strengthen your thinking, one practice at a time.
                </Text>
              </VStack>

              {/* Navigation Links */}
              <VStack align={{ base: 'center', md: 'start' }} spacing={{ base: 3, md: 4 }}>
                <Heading size={{ base: 'sm', md: 'md' }} color="white">
                  Navigation
                </Heading>
                <Button
                  variant="link"
                  color="white"
                  _hover={{ color: "white" }}
                  onClick={() => this.scrollToSection('how-it-works')}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  p={0}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  How it Works
                </Button>
                <Button
                  variant="link"
                  color="white"
                  _hover={{ color: "white" }}
                  onClick={() => this.scrollToSection('reviews')}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  p={0}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  Reviews
                </Button>
                <Button
                  variant="link"
                  color="white"
                  _hover={{ color: "white" }}
                  onClick={() => this.scrollToSection('pricing')}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  p={0}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  Pricing
                </Button>
              </VStack>

              {/* Get Started */}
              <VStack align={{ base: 'center', md: 'start' }} spacing={{ base: 3, md: 4 }}>
                <Heading size={{ base: 'sm', md: 'md' }} color="white">
                  Get Started
                </Heading>
                <Button
                  variant="link"
                  color="white"
                  _hover={{ color: "white" }}
                  onClick={() => this.props.onRouteChange('register')}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  p={0}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  Sign Up Free
                </Button>
                <Button
                  variant="link"
                  color="white"
                  _hover={{ color: "white" }}
                  onClick={() => this.props.onRouteChange('login')}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  p={0}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  Sign In
                </Button>
              </VStack>
            </Grid>

            <Box borderTop="1px" borderColor="whiteAlpha.300" mt={{ base: 8, md: 12 }} pt={{ base: 6, md: 8 }}>
              <Text color="whiteAlpha.800" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                ©2025 <Text as="span" bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">Grow</Text>. All rights reserved.
              </Text>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }
}

export default LandingPage;